import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { isPremium, premiumDaysRemaining } from "../lib/premium";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import {
  Loader2, Sparkles, ShieldCheck, Zap, Camera,
  FileSpreadsheet, FileDown, BarChart3, Crown,
} from "lucide-react";

const FEATURES = [
  { icon: Zap, label: "Unlimited AI value estimates" },
  { icon: Camera, label: "Shelf-scan: identify many models in one photo" },
  { icon: FileSpreadsheet, label: "Bulk CSV/Excel collection import" },
  { icon: FileDown, label: "Insurance-grade PDF valuation reports" },
  { icon: BarChart3, label: "Advanced analytics & price-history charts" },
  { icon: ShieldCheck, label: "Priority support + early access to new features" },
];

function StatusCard({ user }) {
  const premium = isPremium(user);
  const days = premiumDaysRemaining(user);
  const isAdmin = user?.role === "admin";
  const heading = isAdmin ? "Castfolio Admin" : premium ? "Castfolio Premium" : "Free Plan";
  const detail = isAdmin
    ? "Full access — admin accounts bypass premium gates."
    : premium
    ? `${days} day${days === 1 ? "" : "s"} remaining · expires ${new Date(user.premium_expires_at).toLocaleDateString()}`
    : "Upgrade to unlock everything below.";
  return (
    <div
      data-testid="billing-status-card"
      className={`rounded-2xl p-6 ring-1 ${
        premium
          ? "bg-gradient-to-br from-amber-950/40 via-zinc-900 to-zinc-900 ring-amber-500/30"
          : "bg-zinc-900 ring-white/5"
      }`}
    >
      <div className="flex items-center gap-3">
        <Crown className={`w-6 h-6 ${premium ? "text-amber-400" : "text-zinc-600"}`} />
        <div>
          <h2 className="text-zinc-100 font-semibold">{heading}</h2>
          <p className="text-zinc-400 text-sm">{detail}</p>
        </div>
      </div>
    </div>
  );
}

function PackageCard({ pkg, onSelect, busy }) {
  const monthly = pkg.id === "monthly";
  return (
    <div
      data-testid={`billing-package-${pkg.id}`}
      className={`relative rounded-2xl p-6 sm:p-8 ring-1 ring-white/10 ${
        monthly
          ? "bg-zinc-900"
          : "bg-gradient-to-br from-amber-500/10 via-rose-500/10 to-fuchsia-500/10 ring-amber-400/30"
      }`}
    >
      {pkg.badge && (
        <div className="absolute -top-3 left-6 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest rounded-full bg-amber-400 text-black">
          {pkg.badge}
        </div>
      )}
      <h3 className="font-heading text-xl font-bold text-zinc-100">{pkg.label}</h3>
      <div className="flex items-baseline gap-1 mt-3">
        <span className="text-5xl font-black text-white">${pkg.amount}</span>
        <span className="text-zinc-500 text-sm">/{monthly ? "month" : "year"}</span>
      </div>
      {!monthly && (
        <p className="text-xs text-amber-300 mt-1">Just $3.25/month, billed once.</p>
      )}
      <p className="text-zinc-400 text-sm mt-4">{pkg.tagline}</p>
      <Button
        data-testid={`billing-buy-${pkg.id}`}
        onClick={() => onSelect(pkg)}
        disabled={busy}
        className={`w-full mt-6 ${
          monthly
            ? "bg-zinc-800 hover:bg-zinc-700 text-white"
            : "bg-amber-400 hover:bg-amber-300 text-black font-semibold"
        }`}
      >
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
        Get {pkg.label.replace("Premium · ", "")}
      </Button>
    </div>
  );
}

function StatusPoller({ sessionId, onDone }) {
  // Polls /billing/checkout/{id}/status until paid or attempts exhausted
  const [attempts, setAttempts] = useState(0);
  const [state, setState] = useState({ payment_status: "pending" });

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/billing/checkout/${sessionId}/status`);
        if (cancelled) return;
        setState(data);
        if (data.payment_status === "paid") {
          onDone(true, data);
          return;
        }
        if (data.status === "expired") {
          onDone(false, data);
          return;
        }
        if (attempts >= 20) {
          onDone(false, { payment_status: "timeout" });
          return;
        }
        setTimeout(() => setAttempts((a) => a + 1), 2000);
      } catch (e) {
        onDone(false, { error: e?.response?.data?.detail || "Failed to check status" });
      }
    };
    tick();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempts, sessionId]);

  return (
    <div className="flex flex-col items-center text-center py-12" data-testid="billing-poller">
      <Loader2 className="w-10 h-10 text-amber-400 animate-spin mb-4" />
      <p className="text-zinc-200 text-lg">Confirming your payment…</p>
      <p className="text-zinc-500 text-sm mt-1">Status: {state.payment_status || "checking"}</p>
    </div>
  );
}

export default function BillingPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [pollingId, setPollingId] = useState(null);

  useEffect(() => {
    api.get("/billing/packages").then((r) => setPackages(r.data.packages || []));
  }, []);

  // If we land back from Stripe with ?session_id=..., start polling
  useEffect(() => {
    const sid = params.get("session_id");
    if (sid) setPollingId(sid);
  }, [params]);

  const handleBuy = async (pkg) => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        package_id: pkg.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start checkout");
      setBusy(false);
    }
  };

  const handlePollDone = async (ok, data) => {
    if (ok) {
      toast.success("Premium activated. Welcome to Castfolio Premium! 🎉");
      await refreshUser();
    } else if (data?.payment_status === "timeout") {
      toast.error("Payment is taking longer than usual. Refresh in a moment.");
    } else {
      toast.error(data?.error || "Payment was not completed.");
    }
    setPollingId(null);
    setParams({}, { replace: true });
  };

  return (
    <div data-testid="billing-page" className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto space-y-8 animate-fadeIn">
      <div>
        <p className="text-amber-400 text-xs uppercase tracking-[0.3em] font-semibold flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5" /> Castfolio Premium
        </p>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-zinc-100 mt-2">
          Collect harder. Track sharper.
        </h1>
        <p className="text-zinc-400 mt-2 max-w-2xl">
          Premium unlocks unlimited AI valuations, multi-item shelf scanning, bulk CSV/Excel
          import, and insurance-grade PDF reports.
        </p>
      </div>

      <StatusCard user={user} />

      {pollingId ? (
        <Card className="bg-zinc-900 border-zinc-800 rounded-2xl">
          <StatusPoller sessionId={pollingId} onDone={handlePollDone} />
        </Card>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
            {packages.map((p) => (
              <PackageCard key={p.id} pkg={p} onSelect={handleBuy} busy={busy} />
            ))}
          </div>

          <div className="bg-zinc-900/60 ring-1 ring-white/5 rounded-2xl p-6 sm:p-8">
            <h2 className="font-heading text-lg font-semibold text-zinc-100 mb-4">What you get with Premium</h2>
            <ul className="grid sm:grid-cols-2 gap-3">
              {FEATURES.map(({ icon: Icon, label }) => (
                <li key={label} className="flex items-start gap-3 text-zinc-300 text-sm">
                  <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4 text-amber-400" />
                  </div>
                  <span className="pt-1">{label}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="text-center text-zinc-500 text-xs">
            <p>One-time charge · no auto-renewal. Cancel anytime by simply not renewing.</p>
            <p className="mt-1">
              <button onClick={() => navigate("/dashboard")} className="text-zinc-400 hover:text-zinc-200 underline-offset-4 hover:underline">
                Back to Dashboard
              </button>
            </p>
          </div>
        </>
      )}
    </div>
  );
}

// Standalone success page (mounted at /billing/success) — just redirects to /billing with the session_id
export function BillingSuccessRedirect() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  useEffect(() => {
    const sid = params.get("session_id");
    navigate(`/billing${sid ? `?session_id=${sid}` : ""}`, { replace: true });
  }, [params, navigate]);
  return (
    <div className="fixed inset-0 bg-zinc-950 flex flex-col items-center justify-center text-center" data-testid="billing-success-redirect">
      <Loader2 className="w-10 h-10 text-amber-400 animate-spin mb-4" />
      <p className="text-zinc-300">Redirecting…</p>
    </div>
  );
}
